
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Plus } from 'lucide-react';
import { LicenseKey } from '@/types/licenseKey';

interface RecentLicenseKeysCardProps {
  filteredLicenseKeys: LicenseKey[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

const RecentLicenseKeysCard: React.FC<RecentLicenseKeysCardProps> = ({ 
  filteredLicenseKeys, 
  searchQuery, 
  setSearchQuery 
}) => {
  const navigate = useNavigate();
  
  return (
    <div className="mb-8">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Recent License Keys</CardTitle>
              <CardDescription>Recently created or updated license keys</CardDescription>
            </div>
            <div className="mt-4 md:mt-0 flex gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search licenses..."
                  className="pl-8 w-full md:w-64"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button onClick={() => navigate('/master-license/license-keys')}>
                <Plus className="mr-2 h-4 w-4" />
                New License
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium">License Key</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Plan</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Domain</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Assigned To</th>
                </tr>
              </thead>
              <tbody>
                {filteredLicenseKeys.slice(0, 5).map((license) => (
                  <tr key={license.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-mono text-sm">
                        {license.key.substring(0, 10)}...
                      </div>
                    </td>
                    <td className="py-3 px-4 capitalize">
                      <Badge variant={
                        license.plan === 'enterprise' ? 'default' :
                        license.plan === 'expert' ? 'secondary' :
                        license.plan === 'pro' ? 'outline' : 'destructive'
                      }>
                        {license.plan}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      {license.domain || <span className="text-gray-400">Not set</span>}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={license.status === 'active' ? 'success' : 'destructive'}>
                        {license.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      {license.superAdminName || <span className="text-gray-400">Unassigned</span>}
                    </td>
                  </tr>
                ))}
                {filteredLicenseKeys.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-gray-500">
                      No license keys found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
        <CardFooter className="flex justify-center border-t pt-4">
          <Button variant="outline" onClick={() => navigate('/master-license/license-keys')}>
            View All License Keys
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default RecentLicenseKeysCard;
